import React from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";

import { formatChange, formatValue, withSparseSeriesFallback } from "./format";

const CHART_TOOLTIP_STYLE = {
  backgroundColor: "var(--theme-main-bg)",
  border: "1px solid var(--theme-shadow)",
  borderRadius: "10px",
  color: "var(--theme-text)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};

const SummaryStrip = ({ view, totals, siteCount, days }) => {
  const cards = summaryCards(view, totals, siteCount, days);

  return (
    <div>
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
        Aggregate stats
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.label}
            className="relative z-0 overflow-visible rounded-xl border border-theme-secondary bg-theme-main p-4 hover:z-20"
          >
            <div className="text-[11px] font-medium uppercase tracking-wide text-theme-text opacity-50">
              {card.label}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-theme-text">
                {card.display}
              </span>
              {card.change != null && (
                <span className={`text-xs font-medium ${changeClass(card.change)}`}>
                  {formatChange(card.change)}
                </span>
              )}
            </div>
            {card.series?.length > 1 && (
              <div className="mt-2 h-10 overflow-visible [&_.recharts-wrapper]:!overflow-visible">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={card.series}>
                    <Tooltip
                      content={<SummaryTooltip kind={card.tooltipKind || view} />}
                      cursor={{ stroke: "var(--theme-text)", strokeOpacity: 0.25 }}
                      allowEscapeViewBox={{ x: true, y: true }}
                      wrapperStyle={{ zIndex: 30, overflow: "visible", pointerEvents: "none" }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke="var(--theme-text)"
                      fill="var(--theme-text)"
                      fillOpacity={0.08}
                      strokeWidth={1.5}
                      isAnimationActive={false}
                      activeDot={{ r: 3, strokeWidth: 0 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SummaryTooltip = ({ active, payload, kind }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload || {};
  const dateLabel = formatSeriesDate(point.date);
  return (
    <div className="px-2.5 py-2 text-xs" style={CHART_TOOLTIP_STYLE}>
      {dateLabel && (
        <div className="mb-1 text-theme-text opacity-55">{dateLabel}</div>
      )}
      <div className="font-medium text-theme-text">
        {formatSummaryTooltipValue(point.value, kind)}
      </div>
    </div>
  );
};

const formatSeriesDate = (value) => {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const formatSummaryTooltipValue = (value, kind) => {
  if (kind === "speed" || kind === "percent") return formatValue(value, "percent");
  if (kind === "ms") return formatValue(value, "ms");
  if (kind === "cls") return formatValue(value, "cls");
  if (kind === "decimal") return formatValue(value, "decimal");
  if (kind === "growth" || kind === "deploys") {
    const n = Number(value) || 0;
    return `${formatValue(n, "number")} deploy${n === 1 ? "" : "s"}`;
  }
  return formatValue(value, "number");
};

const changeClass = (change) => {
  if (change > 0) return "text-emerald-600";
  if (change < 0) return "text-red-500";
  return "text-theme-text opacity-50";
};

const pctChange = (value, previous) => (
  previous ? ((value - previous) / previous) * 100 : null
);

/** Prefer a dedicated series, else pull a field off the main totals.series. */
const cardSeries = (totals, dedicatedKey, seriesField, fallbackField) => {
  const dedicated = dedicatedKey ? totals?.[dedicatedKey] : null;
  const dedicatedHasSignal = dedicated?.length > 1 && dedicated.some((point) => (point.value || 0) !== 0);
  if (dedicatedHasSignal) return dedicated;
  if (!seriesField) return dedicated || [];
  const fromMain = (totals?.series || []).map((point) => {
    let value = point[seriesField];
    if (value == null && fallbackField) value = point[fallbackField];
    if (value == null && seriesField === "pageviews") value = point.visits ?? point.value;
    return { date: point.date, value: value ?? 0 };
  });
  const mainHasSignal = fromMain.length > 1 && fromMain.some((point) => (point.value || 0) !== 0);
  if (mainHasSignal) return fromMain;
  return dedicated?.length > 1 ? dedicated : fromMain;
};

const summaryCards = (view, totals, siteCount, days) => {
  const value = totals?.value ?? 0;
  const previous = totals?.previous ?? 0;
  const change = pctChange(value, previous);
  const series = totals?.series || [];

  if (view === "traffic") {
    const pageviews = totals?.pageviews ?? 0;
    return [
      {
        label: "Visits",
        display: formatValue(value, "number"),
        change,
        series: withSparseSeriesFallback(series, value),
      },
      {
        label: "Pageviews",
        display: formatValue(pageviews, "number"),
        change: pctChange(pageviews, totals?.previous_pageviews ?? 0),
        series: withSparseSeriesFallback(
          cardSeries(totals, "pageviews_series", "pageviews"),
          pageviews,
        ),
      },
      {
        label: "Pages / visit",
        display: formatValue(totals?.pages_per_visit ?? 0, "decimal"),
        change: null,
        series: totals?.pages_per_visit_series?.length > 1
          ? totals.pages_per_visit_series
          : (totals?.series || []).map((point) => {
              const visits = point.visits ?? point.value ?? 0;
              const pv = point.pageviews ?? 0;
              return {
                date: point.date,
                value: visits ? pv / visits : 0,
              };
            }),
        tooltipKind: "decimal",
      },
    ];
  }

  if (view === "organic") {
    const searchVisits = totals?.search_visits ?? value;
    const aiVisits = totals?.ai_visits ?? 0;
    const searchPageviews = totals?.search_pageviews ?? totals?.pageviews ?? 0;
    const aiPageviews = totals?.ai_pageviews ?? 0;
    return [
      {
        label: "Search visits",
        display: formatValue(searchVisits, "number"),
        change,
        series: withSparseSeriesFallback(
          cardSeries(totals, "search_series", "search"),
          searchVisits,
        ),
      },
      {
        label: "AI visits",
        display: formatValue(aiVisits, "number"),
        change: null,
        series: withSparseSeriesFallback(
          cardSeries(totals, "ai_series", "ai"),
          aiVisits,
        ),
      },
      {
        label: "Search pageviews",
        display: formatValue(searchPageviews, "number"),
        change: null,
        series: withSparseSeriesFallback(
          cardSeries(totals, "search_pageviews_series", "search_pageviews"),
          searchPageviews,
        ),
      },
      {
        label: "AI pageviews",
        display: formatValue(aiPageviews, "number"),
        change: null,
        series: withSparseSeriesFallback(
          cardSeries(totals, "ai_pageviews_series", "ai_pageviews"),
          aiPageviews,
        ),
      },
    ];
  }

  if (view === "speed") {
    return [
      {
        label: "Good LCP",
        display: formatValue(value, "percent"),
        change,
        series,
        tooltipKind: "percent",
      },
      {
        label: "LCP",
        display: formatValue(totals?.lcp, "ms"),
        change: null,
        series: totals?.lcp_series || [],
        tooltipKind: "ms",
      },
      {
        label: "INP",
        display: formatValue(totals?.inp, "ms"),
        change: null,
        series: totals?.inp_series || [],
        tooltipKind: "ms",
      },
      {
        label: "CLS",
        display: totals?.cls != null ? formatValue(totals.cls, "cls") : "N/A",
        change: null,
        series: totals?.cls_series || [],
        tooltipKind: "cls",
      },
    ];
  }

  if (view === "audits") {
    return [
      {
        label: "Pass rate",
        display: formatValue(value, "percent"),
        change,
        series,
        tooltipKind: "percent",
      },
      {
        label: "Pass",
        display: formatValue(totals?.pass ?? 0, "number"),
        change: null,
        series: totals?.pass_series || [],
      },
      {
        label: "Warn",
        display: formatValue(totals?.warn ?? 0, "number"),
        change: null,
        series: totals?.warn_series || [],
      },
      {
        label: "Fail",
        display: formatValue(totals?.fail ?? 0, "number"),
        change: null,
        series: totals?.fail_series || [],
      },
    ];
  }

  if (view === "growth") {
    return [
      {
        label: "Total pages",
        display: formatValue(value, "number"),
        change: null,
        series: [],
      },
      {
        label: "Deploys",
        display: formatValue(totals?.deploy_count ?? 0, "number"),
        change: null,
        series,
        tooltipKind: "deploys",
      },
      {
        label: "Avg pages / site",
        display: formatValue(totals?.avg_pages ?? 0, "number"),
        change: null,
        series: [],
      },
    ];
  }

  return [
    {
      label: "Indexed pages",
      display: "N/A",
      change: null,
      series: [],
    },
    {
      label: "Coverage",
      display: "N/A",
      change: null,
      series: [],
    },
    {
      label: "Status",
      display: "Soon",
      change: null,
      series: [],
    },
  ];
};

export default SummaryStrip;
